import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

/** Shared pagination uses one-based page numbers. */
export function TablePagination({ page = 1, totalPages, totalItems = 0, pageSize = 10, onPageChange, disabled = false, label, first, last }) {
  const { t } = useTranslation();
  const activeButton = useRef(null);
  const pages = Math.max(1, Math.trunc(Number(totalPages ?? Math.ceil(Math.max(0, Number(totalItems) || 0) / Math.max(1, Number(pageSize) || 10))) || 1));
  const current = Math.max(1, Math.min(pages, Math.trunc(Number(page) || 1)));
  const total = Math.max(0, Number(totalItems) || 0);
  const size = Math.max(1, Number(pageSize) || 10);
  const start = total === 0 ? 0 : Math.min((current - 1) * size + 1, total);
  const end = Math.min(current * size, total);
  useEffect(() => {
    const button = activeButton.current;
    const strip = button?.parentElement;
    if (strip) strip.scrollLeft = button.offsetLeft - (strip.clientWidth - button.offsetWidth) / 2;
  }, [current, pages]);
  return (
    <nav className="table-pagination" aria-label={label || t("pagination.navigation", { defaultValue: "Table pagination" })}>
      <span className="table-pagination__summary" aria-live="polite">
        {t("pagination.rowSummary", { defaultValue: "Page {{page}} of {{pages}} ({{start}}-{{end}} of {{total}} total rows)", page: current, pages, start, end, total })}
      </span>
      <div className="table-pagination__controls">
        <button type="button" disabled={disabled || first === true || current <= 1} onClick={() => onPageChange(current - 1)} aria-label={t("pagination.previousPage", { defaultValue: "Previous page" })}>&lsaquo;</button>
        <div className="table-pagination__numbers">
          {Array.from({ length: pages }, (_, index) => index + 1).map(number => (
            <button key={number} ref={number === current ? activeButton : null} type="button" aria-current={number === current ? "page" : undefined} aria-label={t("pagination.goToPage", { defaultValue: "Go to page {{page}}", page: number })} disabled={disabled} onClick={() => onPageChange(number)}>{number}</button>
          ))}
        </div>
        <button type="button" disabled={disabled || last === true || current >= pages} onClick={() => onPageChange(current + 1)} aria-label={t("pagination.nextPage", { defaultValue: "Next page" })}>&rsaquo;</button>
      </div>
    </nav>
  );
}
